import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Copy, Crown, Building2, Target, CheckCircle2, Clock, ArrowRightLeft, X, UserMinus } from "lucide-react";
import { toast } from "sonner";
import ContentRequired from "@/components/ContentRequired";
import { useConfirm } from "@/context/ConfirmContext";
import TeamsReleaseCard from "@/components/TeamsReleaseCard";

const HOLD_WINDOW_MIN = 15;

function fmtCountdown(ms) {
  if (ms <= 0) return "expired";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

const TEAM_LETTERS_ORDER = ["A", "B", "C", "D", "E", "F"];

export default function Teams() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", founding_business_name: "", founding_business_description: "", sector: "" });
  const [movingMember, setMovingMember] = useState(null); // { user_id, name, current_team_id }
  const [moveTargetId, setMoveTargetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [teamCount, setTeamCount] = useState(3);
  const [teamCountBusy, setTeamCountBusy] = useState(false);
  // Re-render every 30s to keep hold-timer countdowns fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    const [tRes, pRes] = await Promise.all([
      api.get("/teams").then((r) => r.data).catch(() => []),
      api.get("/programme").then((r) => r.data).catch(() => ({})),
    ]);
    setTeams(tRes || []);
    setTeamCount(Number(pRes?.team_count) === 6 ? 6 : 3);
  };
  useEffect(() => { load(); }, []);

  const visibleTeams = useMemo(() => {
    const allowed = new Set(TEAM_LETTERS_ORDER.slice(0, teamCount));
    return (teams || []).filter((t) => !t.letter || allowed.has(t.letter));
  }, [teams, teamCount]);

  const changeTeamCount = async (target) => {
    if (target === teamCount) return;
    // If shrinking, check for members on teams that would be hidden
    if (target < teamCount) {
      const hiddenLetters = TEAM_LETTERS_ORDER.slice(target, teamCount);
      const affected = (teams || []).filter((t) => hiddenLetters.includes(t.letter) && (t.members || []).length > 0);
      if (affected.length > 0) {
        const total = affected.reduce((n, t) => n + (t.members?.length || 0), 0);
        const ok = await confirm({
          title: `Shrink to ${target} teams?`,
          description: `Teams ${hiddenLetters.join(", ")} have ${total} member(s). They'll be moved to the Unassigned bucket. This can't be undone automatically — you'll need to reassign them from /roster.`,
          confirmText: `Yes, shrink & unassign ${total}`,
          destructive: true,
        });
        if (!ok) return;
      }
    }
    setTeamCountBusy(true);
    try {
      // Single-source-of-truth save: same endpoint /roster uses. It updates
      // programme_meta.team_count AND provisions/removes team letter rows.
      await api.post("/admin/teams/ensure-count", { count: target });
      setTeamCount(target);
      toast.success(`Running ${target} teams`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update team count");
    } finally {
      setTeamCountBusy(false);
    }
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Team name required");
    try {
      await api.post("/teams", form);
      setOpen(false);
      setForm({ name: "", founding_business_name: "", founding_business_description: "", sector: "" });
      toast.success("Team created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
  };

  const remove = async (id, memberCount) => {
    const msg = memberCount > 0
      ? `Delete this team? Its ${memberCount} member(s) will be unassigned (they'll lose their team and need to rejoin with a code).`
      : "Delete this empty team?";
    if (!(await confirm({ title: msg, destructive: true }))) return;
    await api.delete(`/teams/${id}`);
    toast.success("Team deleted");
    load();
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code || "");
    toast.success("Join code copied");
  };

  const openMove = (member, currentTeamId) => {
    setMovingMember({ user_id: member.id, name: member.name || member.email, current_team_id: currentTeamId, c_suite_role: member.c_suite_role });
    setMoveTargetId("");
  };

  const confirmMove = async () => {
    if (!movingMember || !moveTargetId) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${movingMember.user_id}/move-team`, { team_id: moveTargetId });
      toast.success(`${movingMember.name} moved`);
      setMovingMember(null);
      setMoveTargetId("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally {
      setBusy(false);
    }
  };

  const otherTeams = useMemo(() => {
    if (!movingMember) return [];
    return teams.filter((t) => t.id !== movingMember.current_team_id);
  }, [movingMember, teams]);

  const unassignMember = async (member) => {
    if (!(await confirm({
      title: `Remove ${member.name || member.email} from this team?`,
      description: "They'll be unassigned (no team) but their account stays — they can rejoin with a join code. Use this when someone changed group at the last minute and you don't yet know their new team.",
      confirmText: "Remove from team",
      destructive: true,
    }))) return;
    try {
      await api.post(`/admin/users/${member.id}/move-team`, { team_id: null });
      toast.success(`${member.name || member.email} removed from team`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <div className="space-y-8" data-testid="teams-admin-page">
      <TeamsReleaseCard />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Faculty</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Teams &amp; businesses</h1>
          <p className="text-[#525252] mt-2 max-w-2xl text-sm">One team per business challenge. Share the join code with students so they can sign in and see the workspace. Set a Team Leader from <strong>Student Roster</strong>. Click <strong>Move</strong> on any member to reassign them.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="border border-[#E5E7EB] rounded-none inline-flex" data-testid="teams-admin-count-toggle">
            <span className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#525252] bg-slate-50 border-r border-slate-200 self-center">Cohort teams</span>
            {[3, 6].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => changeTeamCount(c)}
                disabled={teamCountBusy}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${
                  teamCount === c ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
                data-testid={`teams-admin-count-${c}`}
              >{c}</button>
            ))}
          </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="team-create-btn">
              <Plus size={16} className="mr-2" /> Create team
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">New team</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Team name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
                <Field label="Sector" value={form.sector} onChange={(v) => setForm((f) => ({ ...f, sector: v }))} placeholder="e.g. Sustainable apparel" />
              </div>
              <Field label="Business name" value={form.founding_business_name} onChange={(v) => setForm((f) => ({ ...f, founding_business_name: v }))} />
              <div>
                <Label className="label-eyebrow">Challenge statement</Label>
                <Textarea className="mt-2 rounded-none min-h-[120px]" value={form.founding_business_description} onChange={(e) => setForm((f) => ({ ...f, founding_business_description: e.target.value }))} placeholder="A one- or two-sentence brief students will read after the Session 2 reveal." />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="team-save-create-btn">Create team</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {visibleTeams.length === 0 ? (
        <ContentRequired
          title="No teams yet"
          description="One team per business challenge. Students join with the team's code on registration."
          testid="teams-empty-content-required"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleTeams.map((t) => {
            const members = t.members || [];
            const targets = t.target_claims || [];
            const finalised = targets.find((x) => x.finalised);
            const holds = targets.filter((x) => !x.finalised);
            return (
              <div key={t.id} className="border border-[#E5E7EB] bg-white p-5" data-testid={`admin-team-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-xl font-bold tracking-tight truncate">{t.name}</div>
                    {t.sector && <div className="text-xs text-[#525252] mt-1">{t.sector}</div>}
                  </div>
                  <button onClick={() => remove(t.id, members.length)} className="text-[#525252] hover:text-[#e93e36] shrink-0" data-testid={`admin-team-delete-${t.id}`} title="Delete team">
                    <Trash2 size={14} />
                  </button>
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="label-eyebrow">Join code</span>
                  <span className="font-mono bg-[#0A0A0A] text-white px-2 py-0.5 tracking-widest text-xs">{t.join_code}</span>
                  <button onClick={() => copy(t.join_code)} className="text-[#525252] hover:text-[#0A0A0A]" data-testid={`admin-team-copy-${t.id}`}>
                    <Copy size={12} />
                  </button>
                </div>

                {/* Business (admin sees always; students only after Session 2 reveal) */}
                <div className="mt-4 border-t border-[#E5E7EB] pt-3">
                  <div className="label-eyebrow flex items-center gap-1.5"><Building2 size={11} /> Business</div>
                  {t.business_name ? (
                    <div className="text-sm font-medium mt-1" data-testid={`admin-team-business-${t.id}`}>{t.business_name}</div>
                  ) : (
                    <div className="text-xs text-[#A23232] italic mt-1">Not assigned yet</div>
                  )}
                </div>

                {/* Challenge statement */}
                <div className="mt-3 border-t border-[#E5E7EB] pt-3">
                  <div className="label-eyebrow flex items-center gap-1.5"><Target size={11} /> Challenge statement</div>
                  {t.challenge_statement ? (
                    <p className="text-[11px] text-[#525252] mt-1 whitespace-pre-wrap leading-relaxed" data-testid={`admin-team-challenge-${t.id}`}>
                      {t.challenge_statement}
                    </p>
                  ) : (
                    <div className="text-xs text-[#A23232] italic mt-1">Not written yet — add via /my-team as admin</div>
                  )}
                </div>

                {/* Members */}
                <div className="mt-4 border-t border-[#E5E7EB] pt-3">
                  <div className="label-eyebrow">Members ({members.length})</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {members.map((m) => (
                      <div key={m.id} className="border border-[#E5E7EB] bg-[#FAFAFA] px-2.5 py-1.5 flex items-center gap-2" data-testid={`admin-team-member-${m.id}`}>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-[#0A0A0A] flex items-center gap-1">
                            {m.is_team_leader && <Crown size={10} className="text-[#f4b847]" />}
                            <span>{m.name || "(no name)"}</span>
                          </div>
                          <div className="text-[10px] text-[#525252] truncate max-w-[200px]">{m.email}</div>
                        </div>
                        <button
                          onClick={() => openMove(m, t.id)}
                          className="text-[#525252] hover:text-[#2596be] p-1"
                          title="Move to another team"
                          data-testid={`admin-team-move-${m.id}`}
                        >
                          <ArrowRightLeft size={12} />
                        </button>
                        <button
                          onClick={() => unassignMember(m)}
                          className="text-[#525252] hover:text-[#A23232] p-1"
                          title="Remove from team (keep account)"
                          data-testid={`admin-team-unassign-${m.id}`}
                        >
                          <UserMinus size={12} />
                        </button>
                      </div>
                    ))}
                    {members.length === 0 && <span className="text-xs text-[#525252] italic">No members yet</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Move member dialog */}
      <Dialog open={!!movingMember} onOpenChange={(o) => { if (!o) { setMovingMember(null); setMoveTargetId(""); } }}>
        <DialogContent className="rounded-none max-w-md" data-testid="admin-move-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">Move member</DialogTitle>
            <DialogDescription className="text-sm text-[#525252] leading-relaxed">
              Move <strong>{movingMember?.name}</strong> to another team. Their Team Leader status
              (if any) will carry across.
              it will be cleared so they can pick again on Day 1.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="label-eyebrow">Move to team</Label>
            <select
              value={moveTargetId}
              onChange={(e) => setMoveTargetId(e.target.value)}
              className="rounded-none border border-[#0A0A0A] h-11 w-full px-3 text-sm bg-white"
              data-testid="admin-move-team-select"
            >
              <option value="">— Choose target team —</option>
              {otherTeams.map((t) => (
                <option key={t.id} value={t.id}>{t.name} ({t.join_code})</option>
              ))}
            </select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setMovingMember(null); setMoveTargetId(""); }} className="rounded-none border-[#0A0A0A]" data-testid="admin-move-cancel">
              <X size={13} className="mr-2" /> Cancel
            </Button>
            <Button onClick={confirmMove} disabled={!moveTargetId || busy} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="admin-move-confirm">
              <ArrowRightLeft size={13} className="mr-2" /> {busy ? "Moving…" : "Move member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-2 rounded-none h-11" />
    </div>
  );
}
